import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from './pages/Login';
import { HomePage } from './pages/Home';
import { UploadPage } from './pages/Upload';
import { PlansPage } from './pages/Plans';
import { IndividualPlanPage } from './pages/IndividualPlan';
import { ProgramEvaluation } from './pages/ProgramEvaluation';
import {
  CohortDetailPage,
  GradeDetailPage,
  SchoolDetailPage as PeSchoolDetailPage,
  StudentListPage,
  StudentSnapshotPage,
} from './pages/ProgramEvaluation/drilldown';
import {
  fetchDataStatus,
  serverSourcedSubmission,
  type PeDataStatus,
} from './pages/ProgramEvaluation/dataStatus';
import { GenerateChoice } from './pages/Generate/GenerateChoice';
import { DataIntegrationsPage } from './pages/Data';
import { MatchRosterPage } from './pages/Data/MatchRosterPage';
import { MatchesPage } from './pages/Data/MatchesPage';
import { ReportsPage } from './pages/Reports';
import { ImplementationReportPage, IMPLEMENTATION_PRINT_ROUTE, IMPLEMENTATION_ROUTE } from './pages/ImplementationReport';
import { ImplementationReportPrintPage } from './pages/ImplementationReport/PrintPage';
import { AssignmentPage, LibraryPage, LIBRARY_PRINT_ROUTE, LIBRARY_ROUTE } from './pages/AssignmentLibrary';
import { LibraryPrintPage } from './pages/AssignmentLibrary/PrintPage';
import { ProgressReportPage } from './pages/ProgressReport';
import { ProgressReportsListPage } from './pages/ProgressReports';
import { ProgressReportsPrintPage } from './pages/ProgressReports/PrintPage';
import { RosterPage } from './pages/Roster';
import { SamplePlanPage } from './pages/Samples/SamplePlanPage';
import { SampleProgramEvaluationPage } from './pages/Samples/SampleProgramEvaluationPage';
import { SAMPLE_PLAN_ROUTE, SAMPLE_PROGRAM_EVALUATION_ROUTE } from './pages/Samples/routes';
import { StudentProfilePage } from './pages/Roster/StudentProfilePage';
import { SchoolsPage } from './pages/Schools';
import { SchoolDetailPage } from './pages/Schools/SchoolDetailPage';
import { BenchmarksPage } from './pages/Benchmarks';
import { GrowthPage, GrowthStudentPage } from './pages/Growth';
import {
  AssignmentMonitoringMonthPage,
  AssignmentMonitoringSchoolPage,
  AssignmentMonitoringTeacherPage,
} from './pages/AssignmentMonitoring';
import { ComingSoonPage } from './pages/Home/ComingSoonPage';
import { SettingsPage } from './pages/Settings';
import { SETTINGS_EXIT_CRITERIA_ROUTE, SETTINGS_PLANS_ROUTE, SETTINGS_YEARS_ROUTE } from './pages/Settings/routes';
import { ANALYTICS_BETA_ROUTE, ANALYTICS_PERFORMANCE_ROUTE, COMING_SOON_ROUTES, LEGACY_BETA_USAGE_ROUTE, NAV_ROUTES } from './pages/Home/navRoutes';
import { AdminProgramEvaluationsPage } from './pages/Admin';
import { AdminProgramEvaluationReportPage } from './pages/Admin/ReportPage';
import { AdminUploadsPage } from './pages/Admin/UploadsPage';
import { AdminUploadsDetailPage } from './pages/Admin/UploadsDetailPage';
import { AdminUploadFilePage } from './pages/Admin/UploadFilePage';
import { AdminAnalyticsPage } from './pages/Admin/AnalyticsPage';
import { AdminImpersonationPage } from './pages/Admin/ImpersonationPage';
import { AdminProgressReportAuditPage } from './pages/Admin/ProgressReportAuditPage';
import { AdminCriteriaDifferencesPage } from './pages/Admin/CriteriaDifferencesPage';
import { AdminSisMatchingDistrictPage, AdminSisMatchingPage } from './pages/Admin/SisMatching';
import { AdminFeatureFlagsPage } from './pages/Admin/FeatureFlags';
import { BambooRoute, FeatureRoute, PrivateRoute, SuperAdminRoute } from './core/privateRoute';
import { useAuth } from './core/authContext';
import { FEATURE_ASSIGNMENT_LIBRARY } from './core/features';
import { TopBars } from './core/TopBars';

export function ProgramEvaluationRoute() {
  const { isSuperAdmin } = useAuth();
  const [status, setStatus] = useState<PeDataStatus | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    fetchDataStatus()
      .then((next) => {
        if (active) setStatus(next);
      })
      .catch((err) => {
        // An unreachable status endpoint is unknown, not empty: offering a
        // first generation here would regenerate over a district's report.
        console.warn('program-evaluation data status unavailable', err);
      });
    return () => {
      active = false;
    };
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return <ProgramEvaluation missingReportRoute={NAV_ROUTES.reports} />;
  if (status === null) return null;
  return (
    <ProgramEvaluation
      inputsProvider={status.hasData ? serverSourcedSubmission : undefined}
      latestDataSchoolYear={status.schoolYear ?? undefined}
    />
  );
}

export function App() {
  return (
    <BrowserRouter>
      <TopBars />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={(
            <PrivateRoute>
              <HomePage />
            </PrivateRoute>
          )}
        />
        <Route path="/settings" element={<Navigate to={SETTINGS_EXIT_CRITERIA_ROUTE} replace />} />
        <Route
          path={SETTINGS_EXIT_CRITERIA_ROUTE}
          element={(
            <BambooRoute>
              <SettingsPage tab="exit-criteria" />
            </BambooRoute>
          )}
        />
        <Route
          path={SETTINGS_YEARS_ROUTE}
          element={(
            <BambooRoute>
              <SettingsPage tab="years" />
            </BambooRoute>
          )}
        />
        <Route
          path={SETTINGS_PLANS_ROUTE}
          element={(
            <BambooRoute>
              <SettingsPage tab="plans" />
            </BambooRoute>
          )}
        />
        <Route path="/reclassification" element={<Navigate to={SETTINGS_EXIT_CRITERIA_ROUTE} replace />} />
        <Route path="/thresholds" element={<Navigate to={SETTINGS_YEARS_ROUTE} replace />} />
        <Route
          path="/upload"
          element={(
            <BambooRoute>
              <UploadPage />
            </BambooRoute>
          )}
        />
        <Route path="/review" element={<Navigate to="/data/matches" replace />} />
        <Route
          path="/plans"
          element={(
            <BambooRoute>
              <PlansPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/plans/:studentId"
          element={(
            <BambooRoute>
              <IndividualPlanPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/plans/:studentId/:schoolYear"
          element={(
            <BambooRoute>
              <IndividualPlanPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/reports/student-progress"
          element={(
            <BambooRoute>
              <ProgressReportsListPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/reports/student-progress/print"
          element={(
            <BambooRoute>
              <ProgressReportsPrintPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/reports/student-progress/:studentId"
          element={(
            <BambooRoute>
              <ProgressReportPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/generate"
          element={(
            <BambooRoute>
              <GenerateChoice />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation"
          element={(
            <BambooRoute>
              <ProgramEvaluationRoute />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation/grade/:grade"
          element={(
            <BambooRoute>
              <GradeDetailPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation/school/:school"
          element={(
            <BambooRoute>
              <PeSchoolDetailPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation/cohort/:cohort"
          element={(
            <BambooRoute>
              <CohortDetailPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation/students/:key"
          element={(
            <BambooRoute>
              <StudentListPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/program-evaluation/student/:ref/:key"
          element={(
            <BambooRoute>
              <StudentSnapshotPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/reports"
          element={(
            <BambooRoute>
              <ReportsPage />
            </BambooRoute>
          )}
        />
        <Route path={IMPLEMENTATION_ROUTE} element={<BambooRoute><ImplementationReportPage view="district" /></BambooRoute>} />
        <Route path={IMPLEMENTATION_PRINT_ROUTE} element={<BambooRoute><ImplementationReportPrintPage /></BambooRoute>} />
        <Route path={`${IMPLEMENTATION_ROUTE}/school/:schoolId`} element={<BambooRoute><ImplementationReportPage view="school" /></BambooRoute>} />
        <Route path={`${IMPLEMENTATION_ROUTE}/school/:schoolId/teacher/:teacherId`} element={<BambooRoute><ImplementationReportPage view="teacher" /></BambooRoute>} />
        <Route path={LIBRARY_ROUTE} element={<FeatureRoute feature={FEATURE_ASSIGNMENT_LIBRARY}><LibraryPage /></FeatureRoute>} />
        <Route path={LIBRARY_PRINT_ROUTE} element={<FeatureRoute feature={FEATURE_ASSIGNMENT_LIBRARY}><LibraryPrintPage /></FeatureRoute>} />
        <Route path={`${LIBRARY_ROUTE}/:assignmentId`} element={<FeatureRoute feature={FEATURE_ASSIGNMENT_LIBRARY}><AssignmentPage /></FeatureRoute>} />
        <Route
          path={SAMPLE_PROGRAM_EVALUATION_ROUTE}
          element={(
            <BambooRoute>
              <SampleProgramEvaluationPage />
            </BambooRoute>
          )}
        />
        <Route
          path={SAMPLE_PLAN_ROUTE}
          element={(
            <BambooRoute>
              <SamplePlanPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/schools"
          element={(
            <BambooRoute>
              <SchoolsPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/schools/:schoolName"
          element={(
            <BambooRoute>
              <SchoolDetailPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/benchmarks"
          element={(
            <BambooRoute>
              <BenchmarksPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/benchmarks/schools/:schoolId"
          element={(
            <BambooRoute>
              <BenchmarksPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/benchmarks/schools/:schoolId/teachers/:teacherId"
          element={(
            <BambooRoute>
              <BenchmarksPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/roster"
          element={(
            <BambooRoute>
              <RosterPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/roster/:studentId"
          element={(
            <BambooRoute>
              <StudentProfilePage />
            </BambooRoute>
          )}
        />
        <Route
          path="/data"
          element={(
            <BambooRoute>
              <DataIntegrationsPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/data/match/:uploadId"
          element={(
            <BambooRoute>
              <MatchRosterPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/data/matches"
          element={(
            <BambooRoute>
              <MatchesPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/assignments/:month"
          element={(
            <BambooRoute>
              <AssignmentMonitoringMonthPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/assignments/:month/school/:schoolId"
          element={(
            <BambooRoute>
              <AssignmentMonitoringSchoolPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/assignments/:month/school/:schoolId/teacher/:teacherId"
          element={(
            <BambooRoute>
              <AssignmentMonitoringTeacherPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/growth"
          element={(
            <BambooRoute>
              <GrowthPage level="district" />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/growth/grades/:grade"
          element={(
            <BambooRoute>
              <GrowthPage level="grade" />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/growth/schools/:school"
          element={(
            <BambooRoute>
              <GrowthPage level="school" />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/growth/teachers/:teacher"
          element={(
            <BambooRoute>
              <GrowthPage level="teacher" />
            </BambooRoute>
          )}
        />
        <Route
          path="/dashboard/growth/students/:studentId"
          element={(
            <BambooRoute>
              <GrowthStudentPage />
            </BambooRoute>
          )}
        />
        <Route
          path="/admin/program-evaluations"
          element={(
            <SuperAdminRoute>
              <AdminProgramEvaluationsPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/program-evaluations/:sub/:year"
          element={(
            <SuperAdminRoute>
              <AdminProgramEvaluationReportPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/uploads"
          element={(
            <SuperAdminRoute>
              <AdminUploadsPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/uploads/:partition"
          element={(
            <SuperAdminRoute>
              <AdminUploadsDetailPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/uploads/:partition/:year/:version"
          element={(
            <SuperAdminRoute>
              <AdminUploadFilePage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/analytics"
          element={(
            <SuperAdminRoute>
              <AdminAnalyticsPage tab="district" />
            </SuperAdminRoute>
          )}
        />
        <Route
          path={ANALYTICS_BETA_ROUTE}
          element={(
            <SuperAdminRoute>
              <AdminAnalyticsPage tab="beta" />
            </SuperAdminRoute>
          )}
        />
        <Route
          path={ANALYTICS_PERFORMANCE_ROUTE}
          element={(
            <SuperAdminRoute>
              <AdminAnalyticsPage tab="performance" />
            </SuperAdminRoute>
          )}
        />
        <Route path={LEGACY_BETA_USAGE_ROUTE} element={<Navigate to={ANALYTICS_BETA_ROUTE} replace />} />
        <Route
          path="/admin/impersonation"
          element={(
            <SuperAdminRoute>
              <AdminImpersonationPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/progress-report-audit"
          element={(
            <SuperAdminRoute>
              <AdminProgressReportAuditPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/criteria-differences"
          element={(
            <SuperAdminRoute>
              <AdminCriteriaDifferencesPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/sis-matching"
          element={(
            <SuperAdminRoute>
              <AdminSisMatchingPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/sis-matching/:districtId/:field?"
          element={(
            <SuperAdminRoute>
              <AdminSisMatchingDistrictPage />
            </SuperAdminRoute>
          )}
        />
        <Route
          path="/admin/feature-flags"
          element={(
            <SuperAdminRoute>
              <AdminFeatureFlagsPage />
            </SuperAdminRoute>
          )}
        />
        {COMING_SOON_ROUTES.map((path) => (
          <Route
            key={path}
            path={path}
            element={(
              <BambooRoute>
                <ComingSoonPage />
              </BambooRoute>
            )}
          />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
